import { AppContext } from 'src/components/app-context';
import { noop } from 'src/components/base';
import { ViewportTracker } from 'src/components/viewport-tracker';
import queryString from 'querystring';
import React, { memo, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Switch, Route } from 'react-router-dom';
import * as S from './appStyles';
import Feedback from './areas/feedback';
import Footer from './areas/footer';
import MainHeader from './areas/header';
import Sidebar from './areas/sidebar/components/sidebar-desktop';
import addPerformanceTiming from './client/performance-reporter';
import { CoBrandingBanner } from './components/co-branding-banner';
import CookieBanner from './components/cookie-banner';
import ErrorBoundary from './components/error-boundary';
import ErrorMessage from './components/error-message';
import NewsletterSignupModal, {
  NEWSLETTER_PARAM,
} from './components/newsletter-signup-modal';
import ShutdownMessage from './components/shutdown-message';
import StaticIntercom from './components/static-intercom';
import ToastContainer from './components/toast-container';
import './components/toast-container/sagas/toast';
import { TocModal } from './components/toc-modal';
import type { AppState } from './reducers';
import { routeConfig } from './routes/config';
import type { RouteConfig } from './routes/config';
import { useFetchQueryError } from './components/fetch-query-error-context';
import { useLocationContext } from './components/location-context';
import { ErrorPage } from './components/error-page';
import { ScrollToTop } from './components/scroll-to-top';
import { WideContextProvider } from './contexts/wide-context';
import { PageNotFound } from './PageNotFound';

const Error500 = () => <ErrorMessage status={500} />;

const handleGraphqlRequestError = (error: Error) => <ErrorPage error={error} />;

const isRedirectedFromMarketRegex = /(&|^\?)redirect=(ie|it|pl)(&|$)/;

/**
 * A number so that we can track the render after the
 * initial mounting. This is when experiments are loaded and viewportcontext is updated.
 * This is used to add user performance timings.
 */
let renders = 0;

type Props = {
  callback: () => void;
};

const App = ({ callback }: Props) => {
  const location = useLocation();
  const renderToastContainer = useSelector(
    (state: AppState) => state.toast.required
  );
  const { layout = 'default' } = useSelector(
    (state: AppState) => state.globals
  );
  const {
    market: { code, routes },
    renderMode,
  } = useContext(AppContext);
  const { previousLocation } = useLocationContext();
  const { error: fetchQueryError } = useFetchQueryError();

  renders += 1;
  if (renders === 2) {
    addPerformanceTiming('firstRerender');
  }

  const openNewsletterSignup = Object.keys(
    queryString.parse(location.search.substr(1))
  ).includes(NEWSLETTER_PARAM);

  const isRedirectedFromMarket = isRedirectedFromMarketRegex.test(
    location.search
  );
  const redirectedFromMarket =
    isRedirectedFromMarket &&
    (isRedirectedFromMarketRegex.exec(location.search) || [])
      .filter(match => /^(ie|it|pl)$/.test(match))
      .pop();

  const isWebViewLayout = renderMode === 'native';

  const routesWithPaths = useMemo(
    () =>
      Object.entries(routeConfig).reduce<RouteConfig[]>((acc, [key, route]) => {
        acc.push({ ...route, path: routes[key]?.path ?? route.path });
        return acc;
      }, []),
    [routes]
  );

  return (
    <ErrorBoundary onError={Error500}>
      <S.App layout={layout} ref={callback}>
        {openNewsletterSignup && <NewsletterSignupModal />}

        <ViewportTracker>
          {!isWebViewLayout && <MainHeader />}
          {renderToastContainer && <ToastContainer />}
          <Feedback />
          <ErrorBoundary onError={noop}>
            <CoBrandingBanner />
            <CookieBanner />
          </ErrorBoundary>
          <ErrorBoundary onError={noop}>
            <StaticIntercom />
          </ErrorBoundary>
          <ErrorBoundary onError={Error500}>
            <WideContextProvider>
              <S.Main data-test="AppMain" data-test-market-id={code}>
                {!isWebViewLayout && <Sidebar />}

                <S.Content>
                  <ErrorBoundary onError={handleGraphqlRequestError}>
                    <Switch>
                      {routesWithPaths.map(
                        ({
                          path,
                          component,
                          exact,
                          disableScrollOnQueryUpdate,
                        }) => {
                          const RouteComponent = component;

                          return (
                            <Route
                              path={path}
                              exact={exact}
                              key={path.toString()}
                            >
                              <ScrollToTop
                                disableScrollOnQueryUpdate={
                                  disableScrollOnQueryUpdate
                                }
                              />
                              {fetchQueryError && !previousLocation ? (
                                <ErrorPage error={fetchQueryError} />
                              ) : (
                                <RouteComponent />
                              )}
                            </Route>
                          );
                        }
                      )}
                      <Route path="*">
                        <PageNotFound />
                      </Route>
                    </Switch>
                  </ErrorBoundary>
                  {!isWebViewLayout && <Footer />}
                </S.Content>
              </S.Main>
            </WideContextProvider>
          </ErrorBoundary>

          <TocModal />

          {isRedirectedFromMarket && (
            <ShutdownMessage redirectedFromMarket={redirectedFromMarket} />
          )}
        </ViewportTracker>
      </S.App>
    </ErrorBoundary>
  );
};

export default memo(App);
